import { CustomUploadFile } from '@tools/types/commonFields.types.ts';

const isAppendableBlob = (value: unknown): value is Blob => {
  return typeof Blob !== 'undefined' && value instanceof Blob;
};

const sanitizeFilenameSegment = (value: string): string => {
  return value.replace(/[\\/:*?"<>|]/g, ' ').replace(/\s+/g, ' ').trim();
};

const resolveFileExtension = (name: string): string => {
  const match = name.match(/(\.[a-z0-9]+)$/i);
  return match ? match[1] : '';
};

export interface BuildImageUploadFormDataOptions {
  includeCaptions?: boolean;
  useCaptionAsFilename?: boolean;
}

export const buildImageUploadFormData = (
  files: CustomUploadFile[],
  folderId: number | null,
  options: BuildImageUploadFormDataOptions = {},
): FormData | null => {
  const { includeCaptions = false, useCaptionAsFilename = false } = options;
  const formData = new FormData();
  let appended = false;

  for (const file of files) {
    if (!isAppendableBlob(file.originFileObj)) {
      continue;
    }

    const fallbackName =
      file.originFileObj instanceof File && file.originFileObj.name
        ? file.originFileObj.name
        : file.name?.trim() || 'upload';

    const caption = typeof file.caption === 'string' ? file.caption.trim() : '';
    const shouldOverrideName = useCaptionAsFilename && caption.length > 0;
    const sanitizedCaption = shouldOverrideName ? sanitizeFilenameSegment(caption) : '';
    const extension = resolveFileExtension(fallbackName);
    const suggestedName = file.name?.trim();

    const finalName = shouldOverrideName && sanitizedCaption
      ? `${sanitizedCaption}${extension}`
      : suggestedName || fallbackName;

    formData.append('files', file.originFileObj, finalName);
    if (includeCaptions) {
      formData.append('captions', caption);
    }
    appended = true;
  }

  if (!appended) {
    return null;
  }

  if (folderId !== null && folderId !== undefined) {
    formData.append('folder_id', String(folderId));
  }

  return formData;
};

export type BuildImageUploadFormData = typeof buildImageUploadFormData;
